import React, { useState, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Filter, Search, X, Paperclip, Pencil } from 'lucide-react';
import EnhancedLeaseEOIsTable from './EnhancedLeaseEOIsTable';
import { TableColumn, ColumnWidth } from '../../../components/common/EnhancedTable/types';
import './EnhancedLeaseEOIs.css';
import { supabase } from '../../../lib/supabase';
import { formatTextWithLinks } from '../../../utils/formatters';
import { usePermissions } from '../../../hooks/usePermissions';
import LeaseEOIAttachmentsModal from '../../../components/LeaseEOIs/LeaseEOIAttachmentsModal';
import LeaseEOIFormModal from '../../../components/LeaseEOIs/LeaseEOIFormModal';
import type { LeaseEOI, LeaseEOIAttachment } from '../../../types/lease-eoi';
import { formatDate } from '../../../utils/dateUtils';

// Define the sort field type
type SortField = 'eoi_id' | 'eoi_lot' | 'eoi_date' | 'eoi_close_date';

// Define the sort config type
type SortConfig = {
  field: SortField;
  direction: 'asc' | 'desc';
};

// Define the filter type
type LeaseEOIFilter = {
  lot: string | null;
  dateAfter: string | null;
  dateBefore: string | null;
  closeDateBefore: string | null;
};

export default function EnhancedLeaseEOIs() {
  const { canManageSystemSettings, isAdmin } = usePermissions();
  const queryClient = useQueryClient();
  
  // State for filters, sorting, and pagination
  const [searchQuery, setSearchQuery] = useState('');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filters, setFilters] = useState<LeaseEOIFilter>({
    lot: null,
    dateAfter: null,
    dateBefore: null,
    closeDateBefore: null
  });
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    field: 'eoi_date',
    direction: 'desc'
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  
  // State for modals
  const [selectedEoiId, setSelectedEoiId] = useState<number | null>(null);
  const [isAttachmentsModalOpen, setIsAttachmentsModalOpen] = useState(false);
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [selectedEOI, setSelectedEOI] = useState<LeaseEOI | undefined>();
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch lease EOIs data
  const { data: eois = [], isLoading, error: fetchError } = useQuery({
    queryKey: ['lease-eois'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('lease_eois')
        .select(`
          eoi_id,
          eoi_lot,
          eoi_date,
          eoi_doc,
          eoi_close_date
        `)
        .order('eoi_date', { ascending: false });

      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }

      return data || [];
    }
  });

  // Fetch attachments for the selected EOI
  const { data: attachments } = useQuery({
    queryKey: ['lease-eoi-attachments', selectedEoiId],
    queryFn: async () => {
      if (!selectedEoiId) return [];
      
      const { data, error } = await supabase
        .from('lease_eoi_attachments')
        .select('*')
        .eq('eoi_id', selectedEoiId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data;
    },
    enabled: !!selectedEoiId
  });

  // Extract unique values for filter dropdowns
  const uniqueLots = useMemo(() => 
    Array.from(new Set(eois.map(eoi => eoi.eoi_lot))).sort(),
    [eois]
  );

  // Apply filters and search
  const filteredEOIs = useMemo(() => {
    if (!eois) return [];

    return eois.filter(eoi => {
      // Search query filter
      if (searchQuery) {
        const searchLower = searchQuery.toLowerCase();
        const matchesSearch = 
          (eoi.eoi_lot || '').toLowerCase().includes(searchLower) ||
          (eoi.eoi_doc || '').toLowerCase().includes(searchLower);
        
        if (!matchesSearch) return false;
      }

      // Lot filter
      if (filters.lot && eoi.eoi_lot !== filters.lot) {
        return false;
      }

      // Date after filter
      if (filters.dateAfter && eoi.eoi_date < filters.dateAfter) {
        return false;
      }

      // Date before filter
      if (filters.dateBefore && eoi.eoi_date > filters.dateBefore) {
        return false;
      }

      // Close date before filter
      if (filters.closeDateBefore && eoi.eoi_close_date > filters.closeDateBefore) {
        return false;
      }

      return true;
    });
  }, [eois, searchQuery, filters]);

  // Apply sorting
  const sortedEOIs = useMemo(() => {
    return [...filteredEOIs].sort((a, b) => {
      const direction = sortConfig.direction === 'asc' ? 1 : -1;
      
      switch (sortConfig.field) {
        case 'eoi_id': 
          return direction * (a.eoi_id - b.eoi_id);
        case 'eoi_lot': 
          return direction * (a.eoi_lot || '').localeCompare(b.eoi_lot || '');
        case 'eoi_date': 
          return direction * (a.eoi_date || '').localeCompare(b.eoi_date || '');
        case 'eoi_close_date': 
          return direction * (a.eoi_close_date || '').localeCompare(b.eoi_close_date || '');
        default: 
          return 0;
      }
    });
  }, [filteredEOIs, sortConfig]);

  // Apply pagination
  const paginatedEOIs = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return sortedEOIs.slice(startIndex, endIndex);
  }, [sortedEOIs, currentPage, pageSize]);

  // Handle sorting
  const handleSort = (sortConfig: { field: string; direction: 'asc' | 'desc' }) => {
    console.log('Sorting:', sortConfig);
    setSortConfig(sortConfig as SortConfig);
  };

  // Handle sort button clicks
  const handleSortButtonClick = (field: SortField) => {
    setSortConfig(current => ({
      field,
      direction: current.field === field && current.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Handle filter changes
  const handleFilterChange = (newFilters: Partial<LeaseEOIFilter>) => {
    setFilters(current => ({ ...current, ...newFilters }));
  };

  // Handle removing a filter
  const handleRemoveFilter = (key: keyof LeaseEOIFilter) => {
    setFilters(prev => ({ ...prev, [key]: null }));
  };

  // Handle pagination
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1); // Reset to first page when changing page size
  };

  // Handle attachments
  const handleAttachmentsClick = (eoiId: number) => {
    setSelectedEoiId(eoiId);
    setIsAttachmentsModalOpen(true);
  };

  const handleAttachmentsChange = () => {
    queryClient.invalidateQueries({ queryKey: ['lease-eoi-attachments', selectedEoiId] });
  };

  // Handle form submission
  const handleSubmit = async (data: Omit<LeaseEOI, 'eoi_id'>) => {
    setError(null);
    setIsSubmitting(true);
    try {
      if (selectedEOI) {
        // Update existing EOI
        const { error: updateError } = await supabase
          .from('lease_eois')
          .update(data)
          .eq('eoi_id', selectedEOI.eoi_id);
        
        if (updateError) {
          throw updateError;
        }
      } else {
        // Add new EOI
        const { error: insertError } = await supabase
          .from('lease_eois')
          .insert(data);
        
        if (insertError) {
          throw insertError;
        }
      }
      
      await queryClient.invalidateQueries({ queryKey: ['lease-eois'] });
      setIsFormModalOpen(false);
      setSelectedEOI(undefined);
    } catch (err) {
      console.error('Form submission error:', err);
      setError(err instanceof Error ? err.message : 'An error occurred while saving the EOI');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Calculate pagination configuration
  const paginationConfig = {
    currentPage,
    pageSize,
    totalCount: sortedEOIs.length,
    onPageChange: handlePageChange,
    onPageSizeChange: handlePageSizeChange,
    pageSizeOptions: [25, 50, 100, 200]
  };

  // Define table columns
  const columns: TableColumn<LeaseEOI>[] = [
    {
      id: 'actions',
      header: 'Edit',
      accessor: (row) => (
        <div className="action-buttons">
          <button
            onClick={() => {
              setSelectedEOI(row);
              setIsFormModalOpen(true);
            }}
            className="action-button edit"
            title="Edit EOI"
          >
            <Pencil className="h-5 w-5" />
          </button>
        </div>
      ),
      width: 'narrow' as ColumnWidth,
      visibleWhen: (permissions) => permissions.isAdmin || permissions.canManageSystemSettings
    },
    {
      id: 'eoi_id',
      header: 'EOI ID',
      accessor: (row) => (
        <div className="truncate">
          {row.eoi_id}
        </div>
      ),
      width: 'narrow' as ColumnWidth,
      sortable: true
    },
    {
      id: 'eoi_lot',
      header: 'Lot',
      accessor: (row) => (
        <div className="truncate" title={row.eoi_lot || 'Not specified'}>
          {formatTextWithLinks(row.eoi_lot || 'Not specified')}
        </div>
      ),
      width: 'medium' as ColumnWidth,
      sortable: true
    },
    {
      id: 'eoi_date',
      header: 'EOI Date',
      accessor: (row) => {
        const formattedDate = formatDate(row.eoi_date);
        return (
          <div className="truncate" title={formattedDate}>
            {formattedDate}
          </div>
        );
      },
      width: 'medium' as ColumnWidth,
      sortable: true
    },
    {
      id: 'eoi_close_date',
      header: 'Close Date',
      accessor: (row) => {
        const formattedDate = formatDate(row.eoi_close_date);
        return (
          <div className="truncate" title={formattedDate}>
            {formattedDate}
          </div>
        );
      },
      width: 'medium' as ColumnWidth,
      sortable: true
    },
    {
      id: 'eoi_doc',
      header: 'Notes and Docs',
      accessor: (row) => (
        <div className="truncate" title={row.eoi_doc || 'Not specified'}>
          {formatTextWithLinks(row.eoi_doc || 'Not specified')}
        </div>
      ),
      width: 'wide' as ColumnWidth
    }
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-800"></div>
      </div>
    );
  }

  if (fetchError) {
    return (
      <div className="p-4 text-red-500">
        Error loading lease EOI data. Please try again later.
      </div>
    );
  }

  return (
    <div className="enhanced-lease-eois-container p-4">
      <div className="enhanced-lease-eois-header">
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-bold">Lease EOIs</h1>
            
            {/* Add button - only visible to users with admin privileges */}
            {(isAdmin || canManageSystemSettings) && (
              <button 
                onClick={() => {
                  setSelectedEOI(undefined);
                  setIsFormModalOpen(true);
                }}
                className="flex items-center px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark"
              >
                <Plus className="h-5 w-5 mr-2" />
                Add Lease EOI
              </button>
            )}
          </div>

          <div className="flex items-center gap-2">
            {/* Search input */}
            <div className="relative flex-1 max-w-md">
              <input
                type="text"
                placeholder="Search lease EOIs..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
              />
              <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
            </div>
            
            {/* Filter button */}
            <button
              className="p-2 border border-gray-300 rounded-md hover:bg-gray-50 flex items-center gap-1"
              onClick={() => setIsFilterOpen(!isFilterOpen)}
              title="Toggle filters"
            >
              <Filter className="h-5 w-5 text-gray-500" />
              <span className="hidden sm:inline">Filters</span>
              {Object.values(filters).filter(Boolean).length > 0 && (
                <span className="ml-1 bg-primary text-white text-xs rounded-full px-2 py-0.5">
                  {Object.values(filters).filter(Boolean).length}
                </span>
              )}
            </button>
            
            <div className="text-base font-bold text-gray-700">
              Total Records: {filteredEOIs.length}
            </div>
          </div>

          {/* Active filters */}
          {Object.values(filters).some(Boolean) && (
            <div className="active-filters">
              {filters.lot && (
                <div className="filter-tag">
                  <span className="filter-tag-label">Lot:</span>
                  <span>{filters.lot}</span>
                  <button
                    onClick={() => handleRemoveFilter('lot')}
                    className="filter-tag-remove"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              )}
              {filters.dateAfter && (
                <div className="filter-tag">
                  <span className="filter-tag-label">Date After:</span>
                  <span>{formatDate(filters.dateAfter)}</span>
                  <button
                    onClick={() => handleRemoveFilter('dateAfter')}
                    className="filter-tag-remove"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              )}
              {filters.dateBefore && (
                <div className="filter-tag">
                  <span className="filter-tag-label">Date Before:</span>
                  <span>{formatDate(filters.dateBefore)}</span>
                  <button
                    onClick={() => handleRemoveFilter('dateBefore')}
                    className="filter-tag-remove"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              )}
              {filters.closeDateBefore && (
                <div className="filter-tag">
                  <span className="filter-tag-label">Close Before:</span>
                  <span>{formatDate(filters.closeDateBefore)}</span>
                  <button
                    onClick={() => handleRemoveFilter('closeDateBefore')}
                    className="filter-tag-remove"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Filter panel */}
          {isFilterOpen && (
            <div className="filter-panel">
              <div className="filter-panel-header">
                <h3 className="font-medium text-gray-700">Filters</h3>
                <button
                  onClick={() => setFilters({
                    lot: null,
                    dateAfter: null,
                    dateBefore: null,
                    closeDateBefore: null
                  })}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  Clear all
                </button>
              </div>
              <div className="filter-panel-body">
                {/* Lot filter */}
                <div className="filter-group">
                  <label htmlFor="lot-filter">Lot</label>
                  <select
                    id="lot-filter"
                    value={filters.lot || ''}
                    onChange={(e) => handleFilterChange({ lot: e.target.value || null })}
                    className="border border-gray-300 rounded-md p-2 text-sm"
                  >
                    <option value="">All lots</option>
                    {uniqueLots.map(lot => (
                      <option key={lot} value={lot}>{lot}</option>
                    ))}
                  </select>
                </div>
                
                {/* Date After filter */}
                <div className="filter-group">
                  <label htmlFor="date-after-filter">EOI Date After</label>
                  <input
                    id="date-after-filter"
                    type="date"
                    value={filters.dateAfter || ''}
                    onChange={(e) => handleFilterChange({ dateAfter: e.target.value || null })}
                    className="border border-gray-300 rounded-md p-2 text-sm"
                  />
                </div>
                
                {/* Date Before filter */}
                <div className="filter-group">
                  <label htmlFor="date-before-filter">EOI Date Before</label>
                  <input
                    id="date-before-filter"
                    type="date"
                    value={filters.dateBefore || ''}
                    onChange={(e) => handleFilterChange({ dateBefore: e.target.value || null })}
                    className="border border-gray-300 rounded-md p-2 text-sm"
                  />
                </div>
                
                {/* Close Date Before filter */}
                <div className="filter-group">
                  <label htmlFor="close-date-before-filter">Close Date Before</label>
                  <input
                    id="close-date-before-filter"
                    type="date"
                    value={filters.closeDateBefore || ''}
                    onChange={(e) => handleFilterChange({ closeDateBefore: e.target.value || null })}
                    className="border border-gray-300 rounded-md p-2 text-sm"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Sort controls removed - now using column headers for sorting */}
        </div>
      </div>
      
      <div className="enhanced-lease-eois-table-container enhanced-table-force-scroll">
        <EnhancedLeaseEOIsTable
          columns={columns}
          data={paginatedEOIs}
          keyField="eoi_id"
          width="1200px"
          maxHeight="calc(100vh - 20rem)"
          loading={isLoading}
          emptyMessage="No lease EOIs found"
          horizontalScroll={true}
          alternateRowColors={true}
          pagination={paginationConfig}
          defaultSort={sortConfig}
          onSort={handleSort}
          permissions={{
            isAdmin,
            canManageSystemSettings
          }}
        />
      </div>

      {/* Attachments Modal */}
      {selectedEoiId && (
        <LeaseEOIAttachmentsModal
          isOpen={isAttachmentsModalOpen}
          onClose={() => setIsAttachmentsModalOpen(false)}
          eoiId={selectedEoiId}
          attachments={attachments || []}
          onAttachmentsChange={handleAttachmentsChange}
        />
      )}

      {/* Form Modal */}
      <LeaseEOIFormModal
        isOpen={isFormModalOpen}
        onClose={() => {
          setIsFormModalOpen(false);
          setSelectedEOI(undefined);
          setError(null);
        }}
        initialData={selectedEOI}
        onSubmit={handleSubmit}
        isSubmitting={isSubmitting}
        error={error}
        title={selectedEOI ? 'Edit Lease EOI' : 'Add Lease EOI'}
        submitLabel={selectedEOI ? 'Save Changes' : 'Add EOI'}
      />
    </div>
  );
}
